import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import AppShell from "./pages/AppShell";
import PublicLanding from "./pages/PublicLanding";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChoosePlan from "./pages/ChoosePlan";
import PaymentSuccess from "./pages/PaymentSuccess";
import LegalPage from "./pages/LegalPage";
import About from "./pages/About";
import Manifesto from "./pages/Manifesto";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import { CookieBanner } from "./components/CookieBanner";

const Loading = () => (
  <div className="min-h-screen dot-grid flex items-center justify-center">
    <span className="mono-label">LOADING…</span>
  </div>
);

const RequireAuth = ({ children }) => {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

const OnboardingGate = () => {
  const { user } = useAuth();
  if (user.onboarding_completed) return <Navigate to={user.plan ? "/app" : "/choose-plan"} replace />;
  return <Onboarding />;
};

const AppGate = () => {
  const { user } = useAuth();
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  if (!user.plan) return <Navigate to="/choose-plan" replace />;
  return <AppShell />;
};

const PlanGate = () => {
  const { user } = useAuth();
  if (user.plan) return <Navigate to={user.onboarding_completed ? "/app" : "/onboarding"} replace />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return <ChoosePlan />;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AppRouter = () => {
  const location = useLocation();
  // Google auth callback: session_id arrives in the URL fragment — handle it BEFORE any route/auth gate runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/l/:slug" element={<PublicLanding />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="/termini" element={<LegalPage doc="termini" />} />
      <Route path="/cookie" element={<LegalPage doc="cookie" />} />
      <Route path="/about" element={<About />} />
      <Route path="/manifesto" element={<Manifesto />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/choose-plan" element={<RequireAuth><PlanGate /></RequireAuth>} />
      <Route path="/payment/success" element={<RequireAuth><PaymentSuccess /></RequireAuth>} />
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <OnboardingGate />
          </RequireAuth>
        }
      />
      <Route
        path="/app/*"
        element={
          <RequireAuth>
            <AppGate />
          </RequireAuth>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
    <CookieBanner />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="bottom-right" toastOptions={{ style: { fontFamily: "Geist" } }} />
      </AuthProvider>
    </div>
  );
}

export default App;
